import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, FileText, Target, MessageSquare, Users, Sparkles, Image } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LandscapingWebsiteCopywriting = () => {
  const faqs = [
    {
      question: "What is landscaping website copywriting?",
      answer: "Landscaping website copywriting is writing the text on your website—headlines, service descriptions, project pages, and calls-to-action. Good copy positions your work, builds trust, and converts high-ticket leads."
    },
    {
      question: "How much does landscaping website copywriting cost?",
      answer: "Freelance copywriters charge $750–$3,000+ for portfolio-focused websites. Our subscription includes all copywriting in the $200–$300/month price—written by people who understand hardscape and landscape projects."
    },
    {
      question: "Is copywriting included in your website subscription?",
      answer: "Yes. We write all website copy as part of the subscription. You provide project details, services, and what makes you different—we handle the writing, SEO optimization, and conversion focus."
    },
    {
      question: "What makes landscaping copywriting different from general copywriting?",
      answer: "Landscaping copy needs to sell high-ticket projects ($10K–$100K+). It requires understanding of design-build processes, hardscape terminology, and how to position outdoor living projects. Generic copywriters miss these angles."
    },
    {
      question: "How do you write copy for landscaping portfolios?",
      answer: "We write project descriptions that highlight scope, challenges solved, and results. Each project tells a story that helps visitors visualize working with you on their own outdoor space."
    },
    {
      question: "Will the copy help me close bigger projects?",
      answer: "Yes. We position your services for high-ticket sales—emphasizing quality, expertise, and the value of professional landscape design. The copy pre-qualifies leads who understand premium pricing."
    },
    {
      question: "Will the copy be optimized for SEO?",
      answer: "Yes. We write copy that's both conversion-focused AND optimized for local search—proper headings, natural keyword use, and content that helps you rank for landscaping services in your area."
    },
    {
      question: "Can I request changes to the copy after it's written?",
      answer: "Absolutely. Revisions are part of the process. We'll refine copy until it accurately represents your business and positions your services correctly."
    },
    {
      question: "What information do you need from me?",
      answer: "Your service list, service areas, notable projects, what makes you different, testimonials, and preferred tone. We handle the rest—turning your input into professional, high-converting copy."
    },
    {
      question: "How long does it take to write landscaping website copy?",
      answer: "We typically complete all website copy within 3–5 business days as part of our 10–14 day website delivery. Portfolio descriptions may take slightly longer depending on the number of projects."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-copywriting/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Website copywriting for landscaping companies included in our website subscription service."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-copywriting/#webpage",
    "name": "Landscaping Website Copywriting | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/landscaping-website-copywriting/",
    "description": "High-ticket landscaping website copywriting that closes bigger projects. SEO-optimized copy included in subscription.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.leafrushmarketing.com/landscaping-website-copywriting/#service",
    "name": "Landscaping Website Copywriting",
    "provider": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "description": "Conversion-focused website copywriting for landscaping companies, optimized for high-ticket project sales and local SEO.",
    "areaServed": "United States",
    "serviceType": "Website Copywriting"
  };

  return (
    <>
      <SEO 
        title="Landscaping Website Copywriting | Leaf Rush"
        description="High-ticket landscaping website copywriting that closes bigger projects. SEO-optimized copy included in subscription."
        keywords="landscaping website copywriting, landscaping website content, landscaping website copy, landscaper content writing"
        canonical="https://www.leafrushmarketing.com/landscaping-website-copywriting/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-copywriting/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                High-Ticket Focused
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Landscaping Website Copywriting That Closes Bigger Projects
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Professional copy that positions your landscaping work for high-ticket sales. SEO-optimized and included in your subscription.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    Landscaping website copywriting means writing your website's text for high-ticket project sales. Freelancers charge $750–$3,000+. Our subscription includes all copywriting in the $200–$300/month price.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're exploring a complete website solution, visit our main{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary underline hover:no-underline">
                  landscaping website design
                </NavLink>{" "}
                page to see everything included.
              </div>
            </div>
          </div>
        </section>

        {/* What We Write */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What We Write for Your Landscaping Website
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Image className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Project Descriptions</p>
                    <p className="text-sm text-muted-foreground">
                      Compelling copy for each portfolio project that tells the story and sells your expertise.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Target className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Service Page Copy</p>
                    <p className="text-sm text-muted-foreground">
                      Patios, retaining walls, outdoor kitchens—each service positioned for high-ticket conversions.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <FileText className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Homepage Copy</p>
                    <p className="text-sm text-muted-foreground">
                      Headlines and sections that immediately communicate your level of work and expertise.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <MessageSquare className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Calls-to-Action</p>
                    <p className="text-sm text-muted-foreground">
                      Button text and prompts designed to attract qualified consultation requests.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why High-Ticket Copy Matters */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why Landscaping Copy Needs to Sell High-Ticket
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Pre-Qualify Leads</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Copy that attracts $25K–$100K+ projects while filtering out price shoppers.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Position Your Expertise</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Show the difference between you and the guy with a truck. Expertise sells premium pricing.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Tell Project Stories</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Portfolio copy that helps visitors visualize their own project and feel confident hiring you.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground">
                  See{" "}
                  <NavLink to="/landscaping-website-content/" className="text-primary underline hover:no-underline">
                    landscaping website content guide
                  </NavLink>{" "}
                  and{" "}
                  <NavLink to="/landscaping-portfolio-website/" className="text-primary underline hover:no-underline">
                    landscaping portfolio website guide
                  </NavLink>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Copywriting Cost Comparison */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Copywriting Cost Comparison
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Option</th>
                      <th className="text-left py-3 px-4">Cost</th>
                      <th className="text-left py-3 px-4">High-Ticket Focus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Our Subscription</td>
                      <td className="py-3 px-4">Included in $200–$300/mo</td>
                      <td className="py-3 px-4">Yes, landscaping specialists</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">Freelance Copywriter</td>
                      <td className="py-3 px-4">$750–$3,000+ one-time</td>
                      <td className="py-3 px-4">Usually generalist</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4">DIY (Write Yourself)</td>
                      <td className="py-3 px-4">$0 (your time)</td>
                      <td className="py-3 px-4">You know your business</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full pricing:{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>{" "}
                |{" "}
                <NavLink to="/landscaping-website-cost/" className="text-primary underline hover:no-underline">
                  landscaping website cost breakdown
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Landscaping Website Copywriting
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/landscaping-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional landscaping websites.</p>
                </NavLink>
                <NavLink to="/landscaping-website-content/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Content</h3>
                  <p className="text-sm text-muted-foreground">What content you need for your site.</p>
                </NavLink>
                <NavLink to="/landscaping-website-examples/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Landscaping Website Examples</h3>
                  <p className="text-sm text-muted-foreground">See examples of effective landscaping sites.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready for High-Ticket Website Copy?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Book a free call to learn how our subscription includes conversion-focused copywriting for landscapers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
                <NavLink to="/contact/">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteCopywriting;
